// The booking board's cards for the deal flow (ART-326, design 4c): Offer → Hold → Ready to confirm.
//
// Pure — every verb is a callback the page owns — so the per-artist board and the cross-roster board read the same
// words off the same rules, and a test can ask what a card says without mounting a page.
//
//   Offer      soft hold (dashed — only the agency sees it; several on one night group into one card),
//              waiting on artist (sent to the team), passed by team (Delete).
//   Hold       the team is checking with the band — "Hold · 2 of 4 in", "Hold by Dana Whitfield".
//   Ready      the team said yes (Approve, or everyone invited is In) — Confirm show / Cancel show.
import type { DealCard } from "./DealsBoard.js";
import { soonerOf } from "./dealSort.js";
import type { DealPerson } from "../../app/hooks/useBookingOffers.js";
import { formatDay, holdCountdown, holdDaysLeft, offerDealLine } from "./rosterFormat.js";

/** The fields of an offer the board reads (a per-artist `BookingOfferDto` or a roster `RosterOfferRow`). */
export interface FunnelOffer {
  offerId?: string | null;
  date?: string | null;
  buyerVenue?: string | null;
  /** The venue picked in Send to team (ART-330) — the card's title once there is one; until then the buyer. */
  venueName?: string | null;
  amountCents?: number | null;
  dealType?: string | null;
  doorSplitPct?: number | null;
  goodThrough?: string | null;
  status?: string | null;
  sentOn?: string | null;
  decidedBy?: DealPerson | null;
  decisionReason?: string | null;
}

/** The fields of a hold the board reads. */
export interface FunnelHold {
  showId?: string | null;
  date?: string | null;
  venueName?: string | null;
  city?: string | null;
  state?: string | null;
  holdExpiresOn?: string | null;
  guaranteeCents?: number | null;
  ready?: boolean | null;
  inCount?: number | null;
  invitedCount?: number | null;
  decision?: string | null;
  decidedBy?: DealPerson | null;
}

/** "Dana W." — a first name and an initial, the board's compact way of naming a person. */
export function shortName(p: DealPerson | null | undefined): string | null {
  const parts = (p?.name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]![0]}.` : parts[0]!;
}

/** "1d ago" / "just now" from an ISO instant. */
export function ago(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const ms = now.getTime() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const mins = Math.floor(ms / 60_000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export interface OfferCallbacks {
  onOpen: (offer: FunnelOffer) => void;
  onSendToTeam?: (offer: FunnelOffer) => void;
  onPass?: (offer: FunnelOffer) => void;
  onDelete?: (offer: FunnelOffer) => void;
}

/**
 * The Offer column. Soft holds on the SAME night collapse into one card led by the earliest-created
 * ("Soft hold · 3 — also: The Earl · Atlanta, Pilot Light · Knoxville"); its verbs act on the lead offer, and the
 * others are reached by sending the lead (which asks what to do with them) or by opening the card.
 */
export function offerCards(offers: readonly FunnelOffer[], cb: OfferCallbacks): DealCard[] {
  const cards: DealCard[] = [];
  const softByDate = new Map<string, FunnelOffer[]>();
  for (const o of offers) {
    if (o.status === "soft_hold") {
      const key = o.date ?? "";
      softByDate.set(key, [...(softByDate.get(key) ?? []), o]);
    }
  }
  const seenSoft = new Set<string>();

  for (const o of offers) {
    const id = o.offerId ?? "";
    const title = o.venueName || o.buyerVenue || "TBD";
    const deal = offerDealLine(o.amountCents ?? null, o.dealType ?? "guarantee", o.doorSplitPct ?? null);
    if (o.status === "soft_hold") {
      const key = o.date ?? "";
      if (seenSoft.has(key)) continue;
      seenSoft.add(key);
      const group = softByDate.get(key) ?? [o];
      const others = group.slice(1).map((x) => x.venueName || x.buyerVenue || "TBD");
      cards.push({
        key: id,
        sortKey: soonerOf(o.date, o.goodThrough),
        status: "offer",
        tag: group.length > 1 ? `Soft hold · ${group.length}` : "Soft hold",
        tagTone: "soft",
        dashed: true,
        title,
        meta: o.date ? formatDay(o.date) : null,
        detail: others.length > 0
          ? `also: ${others.join(", ")}`
          : o.goodThrough ? `Only visible to your agency · good thru ${formatDay(o.goodThrough)}` : "Only visible to your agency",
        trailing: deal,
        onOpen: () => cb.onOpen(o),
        actions: [
          ...(cb.onSendToTeam ? [{ label: "Send to team", onClick: () => cb.onSendToTeam!(o) }] : []),
          ...(cb.onPass ? [{ label: "Pass", tone: "quiet" as const, onClick: () => cb.onPass!(o) }] : []),
        ],
      });
    } else if (o.status === "requested") {
      cards.push({
        key: id,
        sortKey: soonerOf(o.date, o.goodThrough),
        status: "offer",
        tag: "Waiting on artist",
        tagTone: "wait",
        title,
        meta: o.date ? formatDay(o.date) : null,
        detail: ["Sent to the team", ago(o.sentOn)].filter(Boolean).join(" · "),
        trailing: deal,
        onOpen: () => cb.onOpen(o),
      });
    } else if (o.status === "passed") {
      const who = shortName(o.decidedBy);
      cards.push({
        key: id,
        sortKey: soonerOf(o.date, o.goodThrough),
        status: "offer",
        tag: "Passed by team",
        tagTone: "pass",
        title,
        meta: o.date ? formatDay(o.date) : null,
        detail: [who ? `${o.decidedBy?.name} passed` : "Passed", o.decisionReason ? `“${o.decisionReason}”` : null]
          .filter(Boolean).join(" · "),
        trailing: deal,
        onOpen: () => cb.onOpen(o),
        actions: cb.onDelete ? [{ label: "Delete", tone: "quiet" as const, onClick: () => cb.onDelete!(o) }] : undefined,
      });
    }
  }
  return cards;
}

export interface HoldCallbacks {
  onOpen: (hold: FunnelHold) => void;
  onConfirm?: (hold: FunnelHold) => void;
  onCancel?: (hold: FunnelHold) => void;
}

/** The Hold and Ready-to-confirm columns. Which column is the server's `ready` verdict (`HoldReadiness`). */
export function holdCards(holds: readonly FunnelHold[], cb: HoldCallbacks, money?: (h: FunnelHold) => string | null): DealCard[] {
  return holds.map((h, i) => {
    const id = h.showId ?? `hold-${i}`;
    const title = [h.venueName || "TBD", h.city].filter(Boolean).join(" · ");
    const who = shortName(h.decidedBy);
    const invited = h.invitedCount ?? 0;
    if (h.ready) {
      return {
        key: id,
        sortKey: soonerOf(h.date, h.holdExpiresOn),
        status: "ready",
        tag: "Ready to confirm",
        tagTone: "ready" as const,
        title,
        meta: h.date ? formatDay(h.date) : null,
        detail: h.decision === "approve" && who
          ? `Approved by ${who} · finish the deal with the buyer`
          : "Team is in · finish the deal with the buyer",
        trailing: money?.(h) ?? null,
        onOpen: () => cb.onOpen(h),
        actions: [
          ...(cb.onConfirm ? [{ label: "Confirm show", onClick: () => cb.onConfirm!(h) }] : []),
          ...(cb.onCancel ? [{ label: "Cancel show", tone: "danger" as const, onClick: () => cb.onCancel!(h) }] : []),
        ],
      };
    }
    const exp = h.holdExpiresOn;
    return {
      key: id,
      sortKey: soonerOf(h.date, h.holdExpiresOn),
      status: "hold",
      tag: invited > 0 ? `Hold · ${h.inCount ?? 0} of ${invited} in` : "Hold",
      tagTone: "hold" as const,
      title,
      meta: h.date ? formatDay(h.date) : null,
      detail: who
        ? `Hold by ${who} · checking with band`
        : exp ? `${holdCountdown(exp)} · exp ${formatDay(exp)}` : null,
      metaTone: exp && (holdDaysLeft(exp) ?? 99) <= 3 ? ("hot" as const) : undefined,
      trailing: money?.(h) ?? null,
      onOpen: () => cb.onOpen(h),
    };
  });
}
